import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { TreeTable } from "primereact/treetable";
import useFormState from "../../../../shared/hooks/useFormState";

const esEntero = (tipoDato) => String(tipoDato || '').toUpperCase() === 'ENTERO';
const sanitizeEntero = (v) => v.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');

const FamiliaValoresModal = ({
    visible,
    onHide,
    atributo,
    valores,
    loadingValores,
    saving,
    onGuardarValor,
    onEliminarValor,
}) => {
    const [mode, setMode] = useState("idle"); // 'idle' | 'add' | 'edit'
    const [editingId, setEditingId] = useState(null);
    const inputRef = useRef(null);

    const tipoDato = atributo?.tipoDato || atributo?.tipo_dato || '';
    const esNum = esEntero(tipoDato);

    const { values, errors, setField, reset, validate } = useFormState(
        { valor: '' },
        {
            valor: [(v) => {
                const trimmed = (v || '').trim();
                if (!trimmed) return 'El valor es requerido.';
                if (esNum && !/^-?\d+$/.test(trimmed)) return 'Ingresa solo números enteros.';
                return null;
            }],
        }
    );

    const valido = !errors.valor && values.valor.trim().length > 0;

    useEffect(() => {
        if (!visible) {
            setMode("idle");
            setEditingId(null);
            reset({ valor: '' });
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mode !== "idle") {
            const t = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [mode]);

    const iniciarAgregar = () => {
        reset({ valor: '' });
        setEditingId(null);
        setMode("add");
    };

    const iniciarEditar = (val) => {
        reset({ valor: val.valor || val.nombre || '' });
        setEditingId(val.id);
        setMode("edit");
    };

    const cancelar = () => {
        setMode("idle");
        setEditingId(null);
        reset({ valor: '' });
    };

    const handleChange = (e) => {
        const v = esNum ? sanitizeEntero(e.target.value) : e.target.value;
        setField('valor', v);
    };

    const handleConfirm = async () => {
        if (!validate()) return;
        const payload = { id: mode === "edit" ? editingId : undefined, valor: values.valor.trim() };
        const ok = await onGuardarValor?.(payload);
        if (ok) {
            cancelar();
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelar();
        }
    };

    const treeNodes = valores.map((val, i) => ({
        key: String(i),
        data: {
            id: val.id,
            valor: val.valor || val.nombre || "",
            __editing: mode === "edit" && editingId === val.id,
        },
    }));

    if (mode === "add") {
        treeNodes.unshift({
            key: "__new__",
            data: { id: null, valor: "", __editing: true },
        });
    }

    const valorBody = (rowData) => {
        if (!rowData.data.__editing) {
            return rowData.data.valor;
        }
        return (
            <div className="flex flex-column gap-1">
                <InputText
                    ref={inputRef}
                    value={values.valor}
                    onChange={handleChange}
                    onKeyDown={onKeyDown}
                    className={`w-full${errors.valor ? ' p-invalid' : ''}`}
                    placeholder={esNum ? 'Ej: 10' : 'Ej: ROJO'}
                    inputMode={esNum ? 'numeric' : 'text'}
                    maxLength={esNum ? 10 : 50}
                    disabled={saving}
                />
                {errors.valor && (
                    <small className="p-error block">{errors.valor}</small>
                )}
            </div>
        );
    };

    const actionBody = (rowData) => {
        if (rowData.data.__editing) {
            return (
                <div className="flex gap-1 align-items-center">
                    <Button
                        icon="pi pi-check"
                        tooltip="Guardar"
                        tooltipOptions={{ position: "top" }}
                        onClick={handleConfirm}
                        disabled={!valido || saving}
                        loading={saving}
                        severity="success"
                        size="small"
                    />
                    <Button
                        icon="pi pi-times"
                        tooltip="Cancelar"
                        tooltipOptions={{ position: "top" }}
                        onClick={cancelar}
                        disabled={saving}
                        severity="secondary"
                        size="small"
                        text
                    />
                </div>
            );
        }
        return (
            <div className="flex gap-1 align-items-center">
                <Button
                    outlined
                    severity="primary"
                    icon="pi pi-pencil"
                    tooltip="Editar valor"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => iniciarEditar(rowData.data)}
                    disabled={mode !== "idle"}
                />
                <Button
                    outlined
                    severity="danger"
                    icon="pi pi-trash"
                    tooltip="Eliminar valor"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => onEliminarValor?.(rowData.data)}
                    disabled={mode !== "idle"}
                />
            </div>
        );
    };

    const header = (
        <div className="flex align-items-center justify-content-between">
            <span>
                Valores de: {atributo?.llave || atributo?.nombre || ""}
            </span>
            <Button
                icon="pi pi-plus"
                label="Agregar valor"
                onClick={iniciarAgregar}
                severity="success"
                size="medium"
                disabled={mode !== "idle"}
            />
        </div>
    );

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button className="p-button-text" label="Cancelar" onClick={onHide} />
        </div>
    );

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={onHide}
            style={{ width: '40rem', maxWidth: '95vw' }}
            draggable={false}
            footer={footer}
        >
            <TreeTable
                value={treeNodes}
                loading={loadingValores}
                emptyMessage="No hay valores para este atributo"
                paginator
                rows={10}
            >
                <Column field="valor" header="Valor" expander body={valorBody} />
                <Column body={actionBody} header="" style={{ width: "20%" }} />
            </TreeTable>
        </Dialog>
    );
};

export default FamiliaValoresModal;